/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Medical considerations — the editor.
 *
 * ── THE SCREEN SHOWS ITS OWN EFFECT ─────────────────────────────────────────
 * The panel at the bottom is the important part of this component. A household
 * that records a diagnosis and a medication deserves to see what the app will
 * actually DO about it — including, when the answer is "nothing specific",
 * being told that plainly.
 *
 * Without it this is a form that swallows sensitive data and promises
 * something invisible. With it, the combination rules become visible: enter
 * type 1 diabetes and prednisolone and the screen says, in so many words, that
 * steroids push glucose up and meals will lean lower-GI.
 *
 * ── AND IT NEVER PRESENTS ITSELF AS CLINICAL ────────────────────────────────
 * The disclaimer comes from the server, is shown at the top rather than buried
 * at the bottom, and every guidance string carries the source it came from.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  medicalApi,
  type MedicalConditionOption, type MedicalEntry, type MedicationOption,
} from "../../api/chef.ts";
import { ApiError } from "../../api/client.ts";
import { Icon, IconButton } from "../../components/Icon.tsx";
import { Banner, ErrorBox, Loading } from "../../components/ui.tsx";

export function MedicalEditor() {
  const qc = useQueryClient();
  const state = useQuery({ queryKey: ["medical"], queryFn: () => medicalApi.get() });
  const catalog = useQuery({
    queryKey: ["medical", "catalog"],
    queryFn: () => medicalApi.catalog(),
    // Reference data, identical for every household and ~40KB. Fetch once.
    staleTime: Infinity,
  });

  const [error, setError] = useState<string | null>(null);
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["medical"] });
    // The assistant's context changes with it.
    void qc.invalidateQueries({ queryKey: ["home"] });
  };

  const mutate = <T,>(fn: (arg: T) => Promise<unknown>) =>
    useMutation({
      mutationFn: fn,
      onSuccess: () => { setError(null); invalidate(); },
      onError: (e) => setError(e instanceof ApiError ? e.message : "That didn't work."),
    });

  const addCondition = mutate<{ profileId: string; body: Record<string, unknown> }>(
    ({ profileId, body }) => medicalApi.addCondition(profileId, body));
  const addMedication = mutate<{ profileId: string; body: Record<string, unknown> }>(
    ({ profileId, body }) => medicalApi.addMedication(profileId, body));
  const removeCondition = mutate<string>((id) => medicalApi.removeCondition(id));
  const removeMedication = mutate<string>((id) => medicalApi.removeMedication(id));

  if (state.isLoading || catalog.isLoading) return <Loading label="Loading…" />;
  if (state.error) return <ErrorBox message={(state.error as Error).message} />;
  if (!state.data?.enabled) return null;

  const people = state.data.people;
  const effect = state.data.effect;

  return (
    <div className="space-y-4">
      {/* Top, not bottom. Somebody entering a diagnosis should read this
          before they type, not after. */}
      <Banner tone="info">{state.data.disclaimer}</Banner>

      {people.length === 0 && (
        <p className="text-sm text-ink-3">
          Add the people who eat here first, under "Who's eating", and you'll be
          able to record considerations for each of them.
        </p>
      )}

      {people.map((person) => (
        <section key={person.id} className="card p-4">
          <h3 className="font-medium">{person.name}</h3>

          <EntryList
            title="Conditions"
            entries={person.conditions}
            emptyHint="Nothing recorded."
            onRemove={(id) => removeCondition.mutate(id)}
          />
          <ConditionPicker
            options={catalog.data?.conditions ?? []}
            already={person.conditions.map((c) => c.condition_key ?? "")}
            onAdd={(body) => addCondition.mutate({ profileId: person.id, body })}
          />

          <EntryList
            title="Medications"
            entries={person.medications}
            emptyHint="Nothing recorded."
            onRemove={(id) => removeMedication.mutate(id)}
          />
          <MedicationPicker
            options={catalog.data?.medications ?? []}
            already={person.medications.map((m) => m.medication_key ?? "")}
            onAdd={(body) => addMedication.mutate({ profileId: person.id, body })}
          />
        </section>
      ))}

      {error && <ErrorBox message={error} />}

      {effect && <EffectPanel effect={effect} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function EntryList({ title, entries, emptyHint, onRemove }: {
  title: string;
  entries: MedicalEntry[];
  emptyHint: string;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="mt-3">
      <p className="label-mono text-[11px] uppercase tracking-wider text-ink-3">{title}</p>
      {entries.length === 0 ? (
        <p className="mt-1 text-sm text-ink-3">{emptyHint}</p>
      ) : (
        <ul className="mt-1 divide-y divide-rule-soft">
          {entries.map((e) => (
            <li key={e.id} className="flex items-center gap-2 py-1.5">
              <span className="min-w-0 flex-1 text-sm">
                {e.label_en ?? e.custom_label}
                {!e.label_en && (
                  // Honest about its own limits: free text reaches the
                  // assistant but has no rules behind it.
                  <span className="ml-1.5 text-[11px] text-ink-3">(your own note)</span>
                )}
                {e.ends_on && (
                  <span className="ml-1.5 text-[11px] text-ink-3">until {e.ends_on}</span>
                )}
              </span>
              <IconButton name="trash" label="Remove" tone="danger"
                          onClick={() => onRemove(e.id)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ConditionPicker({ options, already, onAdd }: {
  options: MedicalConditionOption[];
  already: string[];
  onAdd: (body: Record<string, unknown>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pending, setPending] = useState<MedicalConditionOption | null>(null);
  const [endsOn, setEndsOn] = useState("");
  const [custom, setCustom] = useState("");

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return options
      .filter((o) => !already.includes(o.key))
      .filter((o) => !needle
        || o.label_en.toLowerCase().includes(needle)
        || o.summary_en.toLowerCase().includes(needle))
      .slice(0, 12);
  }, [options, already, q]);

  if (!open) {
    return (
      <button className="btn-ghost mt-2 px-2 py-1 text-xs" onClick={() => setOpen(true)}>
        <Icon name="plus" className="mr-1 inline h-3 w-3" />Add a condition
      </button>
    );
  }

  // Time-boxed conditions ask for the date before they can be saved, so a
  // pregnancy stops warning on its own rather than a year later.
  if (pending?.is_time_boxed) {
    return (
      <div className="mt-2 rounded-lg border border-accent/30 bg-accent-soft p-3">
        <p className="text-sm font-medium text-accent">{pending.label_en}</p>
        <label className="mt-2 block text-xs text-ink-2">
          When does this end? (a due date, for example)
          <input type="date" className="input mt-1" value={endsOn}
                 onChange={(e) => setEndsOn(e.target.value)} />
        </label>
        <p className="mt-1 text-[11px] text-ink-3">
          The considerations stop automatically after this date.
        </p>
        <div className="mt-2 flex gap-2">
          <button className="btn-primary px-3 py-1 text-xs" disabled={!endsOn}
                  onClick={() => {
                    onAdd({ conditionKey: pending.key, endsOn });
                    setPending(null); setEndsOn(""); setOpen(false); setQ("");
                  }}>
            Add
          </button>
          <button className="btn-ghost px-2 py-1 text-xs"
                  onClick={() => { setPending(null); setEndsOn(""); }}>
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-rule-soft p-3">
      <input className="input" placeholder="Search conditions…" autoFocus
             value={q} onChange={(e) => setQ(e.target.value)} />
      <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto">
        {matches.map((o) => (
          <li key={o.key}>
            <button
              className="w-full rounded-md px-2 py-1.5 text-left transition hover:bg-paper-3"
              onClick={() => {
                if (o.is_time_boxed) { setPending(o); return; }
                onAdd({ conditionKey: o.key });
                setOpen(false); setQ("");
              }}
            >
              <span className="block text-sm">{o.label_en}</span>
              <span className="block text-xs text-ink-3">{o.summary_en}</span>
            </button>
          </li>
        ))}
        {matches.length === 0 && (
          <li className="px-2 py-1 text-sm text-ink-3">Nothing matches.</li>
        )}
      </ul>

      <div className="mt-2 border-t border-rule-soft pt-2">
        <label className="block text-xs text-ink-2">
          Not listed? Add it in your own words
          <input className="input mt-1" value={custom} placeholder="Something else"
                 onChange={(e) => setCustom(e.target.value)} />
        </label>
        {/* Said before they type it, not after. */}
        <p className="mt-1 text-[11px] text-ink-3">
          The assistant will see this, but the app has no specific guidance behind it.
        </p>
        <div className="mt-2 flex gap-2">
          <button className="btn-ghost px-2 py-1 text-xs" disabled={!custom.trim()}
                  onClick={() => {
                    onAdd({ customLabel: custom.trim() });
                    setCustom(""); setOpen(false); setQ("");
                  }}>
            Add my own
          </button>
          <button className="btn-ghost px-2 py-1 text-xs"
                  onClick={() => { setOpen(false); setQ(""); setCustom(""); }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function MedicationPicker({ options, already, onAdd }: {
  options: MedicationOption[];
  already: string[];
  onAdd: (body: Record<string, unknown>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [custom, setCustom] = useState("");

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return options
      .filter((o) => !already.includes(o.key))
      .filter((o) => !needle
        || o.label_en.toLowerCase().includes(needle)
        // Brand names are how people know their medicines. Someone searching
        // "Jardiance" should find empagliflozin.
        || o.brand_examples.some((b) => b.toLowerCase().includes(needle)))
      .slice(0, 12);
  }, [options, already, q]);

  if (!open) {
    return (
      <button className="btn-ghost mt-2 px-2 py-1 text-xs" onClick={() => setOpen(true)}>
        <Icon name="plus" className="mr-1 inline h-3 w-3" />Add a medication
      </button>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-rule-soft p-3">
      <input className="input" placeholder="Search by name or brand…" autoFocus
             value={q} onChange={(e) => setQ(e.target.value)} />
      <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto">
        {matches.map((o) => (
          <li key={o.key}>
            <button
              className="w-full rounded-md px-2 py-1.5 text-left transition hover:bg-paper-3"
              onClick={() => { onAdd({ medicationKey: o.key }); setOpen(false); setQ(""); }}
            >
              <span className="block text-sm">{o.label_en}</span>
              {o.brand_examples.length > 0 && (
                <span className="block text-xs text-ink-3">
                  {o.brand_examples.join(", ")}
                </span>
              )}
            </button>
          </li>
        ))}
        {matches.length === 0 && (
          <li className="px-2 py-1 text-sm text-ink-3">Nothing matches.</li>
        )}
      </ul>

      <div className="mt-2 border-t border-rule-soft pt-2">
        <label className="block text-xs text-ink-2">
          Not listed? Add it in your own words
          <input className="input mt-1" value={custom} placeholder="Something else"
                 onChange={(e) => setCustom(e.target.value)} />
        </label>
        <div className="mt-2 flex gap-2">
          <button className="btn-ghost px-2 py-1 text-xs" disabled={!custom.trim()}
                  onClick={() => {
                    onAdd({ customLabel: custom.trim() });
                    setCustom(""); setOpen(false); setQ("");
                  }}>
            Add my own
          </button>
          <button className="btn-ghost px-2 py-1 text-xs"
                  onClick={() => { setOpen(false); setQ(""); setCustom(""); }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * What the app will actually do.
 *
 * Combination rules lead, because they are the only guidance that is specific
 * to this household rather than to a diagnosis. Every line names its source —
 * that is what makes the whole table reviewable by somebody clinical, and what
 * stops it reading as the app's own opinion.
 */
function EffectPanel({ effect }: {
  effect: NonNullable<import("../../api/chef.ts").MedicalState["effect"]>;
}) {
  const order = { amplifier: 0, condition: 1, medication: 2 } as const;
  const rules = [...effect.rules].sort((a, b) => order[a.source] - order[b.source]);

  return (
    <section className="card border-accent/25 bg-accent-soft/40 p-4">
      <h3 className="font-medium">What this changes</h3>

      {rules.length === 0 && effect.customEntries.length === 0 && (
        <p className="mt-1 text-sm text-ink-3">
          Nothing recorded yet. Once you add something, this is where you'll see
          exactly what the app does about it.
        </p>
      )}

      {effect.prefer.length > 0 && (
        <p className="mt-2 text-sm text-ink-2">
          Meals will lean toward:{" "}
          <span className="text-ink">{effect.prefer.join(", ").replace(/_/g, " ")}</span>.
        </p>
      )}

      <ul className="mt-3 space-y-2.5">
        {rules.map((r, i) => (
          <li key={i} className="border-l-2 border-accent/40 pl-3">
            <p className="text-sm">
              <span className="font-medium">
                {r.source === "amplifier" ? "Because these combine" : r.title}
              </span>
              {r.people.length > 0 && (
                <span className="text-ink-3"> · {r.people.join(", ")}</span>
              )}
              {r.severity === "warn" && (
                <span className="ml-1.5 rounded bg-warn/20 px-1 text-[10px] uppercase tracking-wide text-ink-2">
                  important
                </span>
              )}
            </p>
            <p className="mt-0.5 text-xs text-ink-2">{r.guidance}</p>
            <p className="mt-0.5 text-[10px] text-ink-3">Source: {r.sourceNote}</p>
          </li>
        ))}
      </ul>

      {effect.customEntries.length > 0 && (
        <p className="mt-3 border-t border-rule-soft pt-2 text-xs text-ink-3">
          The assistant also sees these, but the app has no specific guidance for
          them: {effect.customEntries.join("; ")}.
        </p>
      )}
    </section>
  );
}
